import { map } from "nanostores";
import { CODE_CANCELED, CODE_OK, CODE_UNKNOWN, TaskStatus, TaskType } from "./task.ts";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { downloadWithStream } from "../../utils/request.ts";
import { CacheKV } from "../../utils/cache_kv.ts";
import type { FFMessageLoadConfig } from "@ffmpeg/ffmpeg";
import type { MapStore } from "nanostores";
import type { Task, TaskStore } from "./task.ts";

const cacheName = "ffmpeg-cache";
const cacheKey = {
    version: "version",
    core: "core",
    wasm: "wasm",
    // classWorker is not cached anymore, we use a CDN proxy
    mtWorker: "mtWorker", 
};

const getStaticBase = () => {
    // @ts-ignore
    return window.APP_CONFIG?.STATIC_BASE ?? "";
};

// Single Threaded (ST) resources (Default for environments without SharedArrayBuffer)
const ST_resourceVersion = "st-0.12.10-v12"; // Bump version for fix
const ST_coreURL = () => `${getStaticBase()}/lib/ffmpeg/st/ffmpeg-core.js`;
const ST_wasmURL = () => `${getStaticBase()}/lib/ffmpeg/st/ffmpeg-core.wasm`;

// Multi Threaded (MT) resources (Requires SharedArrayBuffer, COOP/COEP)
const MT_resourceVersion = "mt-0.12.10-v8";
const MT_coreURL = () => `${getStaticBase()}/lib/ffmpeg/mt/ffmpeg-core.js`;
const MT_wasmURL = () => `${getStaticBase()}/lib/ffmpeg/mt/ffmpeg-core.wasm`;
const MT_workerURL = () => `${getStaticBase()}/lib/ffmpeg/mt/ffmpeg-core.worker.js`;

// FFmpeg Class Worker (Orchestrator)
// We use the ESM version from local. To avoid relative import issues in Blob, 
// we create a proxy Blob that imports from the local path.
// MUST match the version of @ffmpeg/ffmpeg installed in package.json/deno.jsonc (0.12.15)
const classWorkerCDN = () => `${getStaticBase()}/lib/ffmpeg/worker.js`;
const classWorkerClassicCDN = () => `${getStaticBase()}/lib/ffmpeg/worker.classic.js`;

export class FFmpegLoadTask implements Task {
    $store: MapStore<TaskStore>;
    #ffmpeg: FFmpeg;
    #ac: AbortController;
    #cachedBinary: Blob[] = [];
    constructor() {
        this.$store = map<TaskStore>({
            _timestampAdd: 0,
            id: "",
            type: TaskType.LoadFFmpeg,
            status: TaskStatus.Pending,
            info: "",
            current: 0,
            total: 0,
            errorCode: CODE_OK,
        });
        this.#ffmpeg = new FFmpeg();
        this.#ac = new AbortController();
        // check if can load from cache, and load as fast as possible
        (async () => {
            try {
                const useSingleThread = !window.crossOriginIsolated;
                const currentVersion = useSingleThread ? ST_resourceVersion : MT_resourceVersion;

                // Extra check for Cache API support to avoid "undefined is not an object" errors
                if (typeof self === 'undefined' || !self.caches) {
                    return;
                }

                const kv = await CacheKV.open(cacheName);
                const cachedVersion = await kv.getString(cacheKey.version, "");
                if (cachedVersion === currentVersion) {
                    this.start();
                }
            } catch (e) {
                console.warn("Auto-load from cache failed (harmless):", e);
            }
        })();
    }
    async loadAction() {
        this.$store.setKey("info", "Checking crossOriginIsolated...");
        const useSingleThread = !window.crossOriginIsolated;
        const currentVersion = useSingleThread ? ST_resourceVersion : MT_resourceVersion;
        
        if (useSingleThread) {
             console.warn("SharedArrayBuffer is not available. Falling back to single-threaded mode (slower).");
             this.$store.setKey("info", "SharedArrayBuffer unavailable. Using Single-Threaded mode...");
        }

        if (this.#cachedBinary.length <= 0) {
            this.$store.setKey("info", "Checking cache...");
            // check cache
            let cacneLoaded = false;
            
            // Extra check for Cache API support
            if (typeof self !== 'undefined' && self.caches) {
                const kv = await CacheKV.open(cacheName);
                const cachedVersion = await kv.getString(cacheKey.version, "");
                
                if (cachedVersion === currentVersion) {
                    try {
                        this.$store.setKey("info", "Loading from cache...");
                        // load from cache
                        const loadFromCache = async (key: string, mime: string) => {
                            const data = await kv.get(key) as ArrayBuffer;
                            return new Blob([data], { type: mime });
                        };
                        
                        if (useSingleThread) {
                             this.#cachedBinary = await Promise.all([
                                loadFromCache(cacheKey.core, "text/javascript"),
                                loadFromCache(cacheKey.wasm, "application/wasm"),
                            ]);
                        } else {
                             // MT needs 3 items (core, wasm, mtWorker)
                             this.#cachedBinary = await Promise.all([
                                loadFromCache(cacheKey.core, "text/javascript"),
                                loadFromCache(cacheKey.wasm, "application/wasm"),
                                loadFromCache(cacheKey.mtWorker, "text/javascript"),
                            ]);
                        }
                        // Basic check
                        if (useSingleThread && this.#cachedBinary.length === 2) {
                             cacneLoaded = true;
                        } else if (!useSingleThread && this.#cachedBinary.length === 3) {
                             cacneLoaded = true;
                        }
                    } catch (err) {
                        console.error(err);
                    }
                }
            }
            
            if (!cacneLoaded) {
                this.$store.setKey("info", "Downloading resources...");
                // download resource
                this.$store.setKey("total", -1);
                this.$store.setKey("current", 0);
                let curr = 0;
                let total = 0;
                this.#ac.abort();
                this.#ac = new AbortController();
                const downloadAction = async (url: string, mime: string) => {
                    const fullUrl = new URL(url, document.baseURI).href;
                    console.log(`Downloading FFmpeg resource: ${fullUrl}`);

                    const dataList: Uint8Array[] = [];
                    let isFirst = true;
                    for await (const prog of downloadWithStream(fullUrl, { mode: "cors", signal: this.#ac.signal })) {
                        dataList.push(prog.data);
                        if (isFirst) {
                            isFirst = false;
                            if (prog.total >= 0) {
                                total += prog.total;
                            }
                            this.$store.setKey("total", total);
                        }
                        curr += prog.data.byteLength;
                        this.$store.setKey("current", curr);
                    }
                    return new Blob(dataList as BufferSource[], { type: mime });
                };
                
                if (useSingleThread) {
                    this.#cachedBinary = await Promise.all([
                        downloadAction(ST_coreURL(), "text/javascript"),
                        downloadAction(ST_wasmURL(), "application/wasm"),
                    ]);
                } else {
                    this.#cachedBinary = await Promise.all([
                        downloadAction(MT_coreURL(), "text/javascript"),
                        downloadAction(MT_wasmURL(), "application/wasm"),
                        downloadAction(MT_workerURL(), "text/javascript"),
                    ]);
                }
                
                if (typeof self !== 'undefined' && self.caches) {
                    const kv = await CacheKV.open(cacheName);
                    await kv.set(cacheKey.core, this.#cachedBinary[0]);
                    await kv.set(cacheKey.wasm, this.#cachedBinary[1]);
                    if (!useSingleThread) {
                        await kv.set(cacheKey.mtWorker, this.#cachedBinary[2]);
                    }
                    
                    await kv.setString(cacheKey.version, currentVersion);
                }
            }
        }
        this.$store.setKey("info", "Initializing FFmpeg...");
        this.$store.setKey("info", "Preparing FFmpeg runtime...");
        const config: FFMessageLoadConfig & { workerType?: "classic" | "module" } = {};
        const objectUrlsToRevoke: string[] = [];
        
        // Construct classWorkerBlob (Proxy)
        // This blob imports the worker script from the CDN.
        // It allows the worker to resolve relative imports against the CDN (if any)
        // and bypasses the UMD/Module mismatch issues in Blob URLs.
        // const classWorkerProxyContent = `import "${classWorkerCDN}";`;
        // const classWorkerBlob = new Blob([classWorkerProxyContent], { type: "text/javascript" });
        // config.classWorkerURL = URL.createObjectURL(classWorkerBlob);
        
        // Try direct worker URL for local file
        // Since we are loading from same origin, we can pass the URL directly
        // But @ffmpeg/ffmpeg might expect a blob url or absolute url.
        // Let's resolve the absolute URL for classWorkerCDN
        
        const resolveUrl = (path: string) => new URL(path, document.baseURI).href;
        const makeObjectURL = (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            objectUrlsToRevoke.push(url);
            return url;
        };

        const supportsModuleWorker = () => {
            try {
                const blob = new Blob(["export {};"], { type: "text/javascript" });
                const url = URL.createObjectURL(blob);
                const w = new Worker(url, { type: "module" });
                w.terminate();
                URL.revokeObjectURL(url);
                return true;
            } catch (_) {
                return false;
            }
        };

        if (useSingleThread) {
            const canModuleWorker = supportsModuleWorker();
            config.workerType = canModuleWorker ? "module" : "classic";
            config.classWorkerURL = resolveUrl(canModuleWorker ? classWorkerCDN() : classWorkerClassicCDN());
            config.coreURL = makeObjectURL(this.#cachedBinary[0]);
            config.wasmURL = makeObjectURL(this.#cachedBinary[1]);
        } else {
            config.workerType = "module";
            config.classWorkerURL = resolveUrl(classWorkerCDN());
            // MT pthread worker has `import("./ffmpeg-core.js")`, so it must keep a URL base where relative imports work.
            // Use absolute network URLs here and rely on HTTP cache / Service Worker caching.
            config.coreURL = resolveUrl(MT_coreURL());
            config.wasmURL = resolveUrl(MT_wasmURL());
            config.workerURL = resolveUrl(MT_workerURL());
        }
        
        this.$store.setKey("info", `Initializing FFmpeg (${useSingleThread ? "ST" : "MT"} / ${config.workerType ?? "module"})...`);

        const loadWithTimeout = async (cfg: FFMessageLoadConfig) => {
             // Use 120s timeout to avoid slow network issues
             const timeoutMs = 120000;
             console.log("Loading FFmpeg with config:", cfg);
             
             // Add event listener for logs to debug loading process
             const logHandler = ({ message }: { message: string }) => {
                 console.log("FFmpeg Log:", message);
                 this.$store.setKey("info", `FFmpeg: ${message}`);
             };
             this.#ffmpeg.on("log", logHandler);
             
             try {
                 return await Promise.race([
                     this.#ffmpeg.load(cfg),
                     new Promise((_, reject) => setTimeout(() => reject(new Error(`FFmpeg load timeout (${timeoutMs}ms)`)), timeoutMs))
                 ]);
             } finally {
                 this.#ffmpeg.off("log", logHandler);
             }
        };

        const MAX_RETRY = 3;
        let lastError: unknown;
        let switchedToClassicWorker = false;
        let switchedToNetworkUrls = false;
        for (let i = 0; i < MAX_RETRY; i++) {
             try {
                 if (i > 0) {
                      this.$store.setKey("info", `Initializing FFmpeg (Retry ${i}/${MAX_RETRY})...`);
                      // Wait 1s before retry
                      await new Promise(r => setTimeout(r, 1000));
                 }
                 await loadWithTimeout(config);
                 lastError = undefined;
                 break;
             } catch (err) {
                console.warn(`FFmpeg load failed (attempt ${i + 1}):`, err);
                if (useSingleThread && config.workerType === "module" && !switchedToClassicWorker) {
                    switchedToClassicWorker = true;
                    config.workerType = "classic";
                    config.classWorkerURL = resolveUrl(classWorkerClassicCDN());
                    this.$store.setKey("info", "FFmpeg module worker unsupported. Switching to classic worker...");
                    lastError = err;
                    continue;
                }
                if (useSingleThread && !switchedToNetworkUrls) {
                    switchedToNetworkUrls = true;
                    config.workerType = "classic";
                    config.classWorkerURL = resolveUrl(classWorkerClassicCDN());
                    config.coreURL = resolveUrl(ST_coreURL());
                    config.wasmURL = resolveUrl(ST_wasmURL());
                    this.$store.setKey("info", "FFmpeg load failed. Switching to network URLs (iOS Chrome fallback)...");
                    lastError = err;
                    continue;
                }
                // Try to extract useful message from event if possible
                if (err instanceof Event && err.type === "error") {
                     lastError = new Error("Worker loading failed (Event: error). Check CSP or CORS.");
                } else {
                     lastError = err;
                }
            }
        }

        for (const url of objectUrlsToRevoke) {
            try {
                URL.revokeObjectURL(url);
            } catch (_) {
                // ignore
            }
        }

        if (lastError) {
             throw lastError;
        }
        this.$store.setKey("total", -1);
    }
    async reload() {
        await this.loadAction();
    }
    start() {
        if (this.$store.get().status === TaskStatus.Pending) {
            this.$store.setKey("status", TaskStatus.Running);
            (async () => {
                try {
                    await this.loadAction();
                    this.$store.setKey("status", TaskStatus.Finished);
                } catch (err) {
                    console.error(err);
                    this.$store.setKey("status", TaskStatus.Error);
                    let errorMsg = String(err);
                    if (errorMsg.includes("timeout")) {
                        errorMsg += " (Please check your network connection)";
                    }
                    this.$store.setKey("info", errorMsg); // Add error info
                    if (this.#ac.signal.aborted) {
                        this.$store.setKey("errorCode", CODE_CANCELED);
                    } else {
                        this.$store.setKey("errorCode", CODE_UNKNOWN);
                    }
                }
            })();
        }
    }
    cancel() {
        this.#ac.abort();
    }
    getFFmpeg() {
        return this.#ffmpeg;
    }
}

// export const transcode = async () => {
//     // await ffmpeg.exec(["-i", "input.webm", "output.mp4"]);
//     await ffmpeg.exec(["-encoders"]);
//     await ffmpeg.exec(["-decoders"]);
// };

export const ffmpegRemoveDir = async (ff: FFmpeg, dir: string) => {
    try {
        const files = await ff.listDir(dir);
        for (const f of files) {
            const subPath = dir + (dir.endsWith("/") ? "" : "/") + f.name;
            if (f.isDir) {
                ffmpegRemoveDir(ff, subPath);
            } else {
                await ff.deleteFile(subPath);
            }
        }
        await ff.deleteDir(dir);
    } catch (_) {
        // ignore error
    }
};
